/**
 * Story 052 — useExpandedMe hook.
 *
 * Mirrors `@aeriosiq/auth-client`'s `useMe` but expects the auth/v1 4.0.0 expanded
 * envelope per `docs/backend-requirements/035-me-expanded.md`. The two
 * hooks coexist: `useMe` is the load-bearing primitive for the auth
 * store + sidebar; `useExpandedMe` is the Settings-page fetcher. Both
 * call the same endpoint — the wire response is the same, the FE just
 * narrows differently.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuthStore, getAuthBaseUrl } from "@aeriosiq/auth-client";
import { accountKeys } from "./keys.js";
import type { MeExpanded } from "./types.js";

async function fetchExpandedMe(
  token: string,
  tenantSlug: string,
): Promise<MeExpanded> {
  const res = await fetch(`${getAuthBaseUrl()}/me`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "X-Tenant-ID": tenantSlug,
    },
  });
  if (!res.ok) {
    const err = new Error(`/me failed: ${String(res.status)}`) as Error & {
      status?: number;
    };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as MeExpanded;
}

export function useExpandedMe(): {
  data: MeExpanded | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
} {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const tenantMatches =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;
  const accessToken = session?.accessToken;
  const tenantSlug = tenant?.tenantSlug;
  const query = useQuery<MeExpanded>({
    queryKey: accountKeys.me(),
    queryFn: (): Promise<MeExpanded> => {
      if (!accessToken || !tenantSlug) {
        throw new Error("useExpandedMe queryFn invoked without session/tenant");
      }
      return fetchExpandedMe(accessToken, tenantSlug);
    },
    enabled: !!session && !!tenant && tenantMatches,
    staleTime: 30_000,
    retry: false,
  });
  return {
    data: query.data,
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
}
